/**
 * A biblioteca de trabalhos.
 *
 * Duas abas, e a diferença entre elas é quem olha:
 *
 * - **Vitrine** — o que qualquer visitante vê: o resumo de cada trabalho, o autor,
 *   a instituição e quanto custa consultar. O texto fica fechado.
 * - **Meus trabalhos** — a área do autor: o que ele publicou, em que estado está
 *   (em análise, publicado, recusado), quantas consultas já teve e quanto tem a
 *   receber. Só aparece para quem está logado.
 *
 * O trabalho em si abre em `/biblioteca/:id`, que é a página que o leitor vê antes
 * de decidir pagar.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ExternalLink,
  FileText,
  Loader2,
  Search,
  Star,
  TriangleAlert,
  Wallet,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api, ErroApi } from '@/lib/api';
import {
  ROTULO_ESTADO_DA_PUBLICACAO,
  ROTULO_NIVEL_DA_BIBLIOTECA,
  precoFormatado,
  type PublicacaoComVendas,
  type PublicacaoDaBiblioteca,
} from '@/lib/biblioteca';
import { useAuth } from '@/auth/AuthContext';
import { cn } from '@/lib/utils';

const NIVEIS = ['GRADUACAO', 'POS', 'DOUTORADO'] as const;

export function Biblioteca() {
  const { usuario } = useAuth();
  const [aba, setAba] = useState<'VITRINE' | 'MEUS'>('VITRINE');

  const [publicacoes, setPublicacoes] = useState<PublicacaoDaBiblioteca[] | null>(null);
  const [minhas, setMinhas] = useState<PublicacaoComVendas[] | null>(null);
  const [busca, setBusca] = useState('');
  const [nivel, setNivel] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  const carregarVitrine = useCallback(async () => {
    setCarregando(true);
    try {
      const { publicacoes: lista } = await api.listarBiblioteca({ busca, nivel });
      setPublicacoes(lista);
      setErro('');
    } catch (causa) {
      setPublicacoes([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar a biblioteca.');
    } finally {
      setCarregando(false);
    }
  }, [busca, nivel]);

  const carregarMinhas = useCallback(async () => {
    setCarregando(true);
    try {
      const { publicacoes: lista } = await api.minhasPublicacoes();
      setMinhas(lista);
      setErro('');
    } catch (causa) {
      setMinhas([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar seus trabalhos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (aba === 'VITRINE') void carregarVitrine();
    else void carregarMinhas();
  }, [aba, carregarVitrine, carregarMinhas]);

  const totalAReceber = useMemo(
    () => (minhas ?? []).reduce((soma, item) => soma + (item.vendas.aReceberCentavos ?? 0), 0),
    [minhas],
  );

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <BookOpen className="size-5" />
        </span>
        <h1 className="text-2xl font-semibold tracking-tight">Biblioteca de trabalhos</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Trabalhos acadêmicos que os próprios autores colocaram à disposição. O resumo, o abstract e os
          resumos em outros idiomas são <strong className="text-foreground">abertos</strong> — é por eles
          que você decide se o trabalho interessa. O texto completo abre com a consulta.
        </p>
      </header>

      {usuario ? (
        <div className="flex gap-2">
          {(
            [
              ['VITRINE', 'Vitrine'],
              ['MEUS', 'Meus trabalhos'],
            ] as const
          ).map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              onClick={() => setAba(valor)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                aba === valor
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/40',
              )}
            >
              {rotulo}
            </button>
          ))}
        </div>
      ) : null}

      {erro ? (
        <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {aba === 'VITRINE' ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <label className="text-xs font-medium text-muted-foreground">Buscar</label>
              <div className="mt-1 flex items-center gap-2 rounded-md border border-input bg-background px-3">
                <Search className="size-4 text-muted-foreground" />
                <input
                  value={busca}
                  onChange={(evento) => setBusca(evento.target.value)}
                  placeholder="Título, autor, instituição ou curso"
                  className="h-9 w-full bg-transparent text-sm outline-none"
                />
              </div>
            </div>
            <select
              value={nivel}
              onChange={(evento) => setNivel(evento.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Todos os níveis</option>
              {NIVEIS.map((item) => (
                <option key={item} value={item}>
                  {ROTULO_NIVEL_DA_BIBLIOTECA[item]}
                </option>
              ))}
            </select>
            <Button variant="contorno" onClick={() => void carregarVitrine()} disabled={carregando}>
              {carregando ? <Loader2 className="animate-spin" /> : <Search />}
              Buscar
            </Button>
          </div>

          {publicacoes === null ? (
            <div className="flex min-h-[20vh] items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span className="text-sm">Carregando a biblioteca...</span>
            </div>
          ) : publicacoes.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Nenhum trabalho por aqui ainda</CardTitle>
                <CardDescription>
                  A biblioteca está começando. Se você tem um trabalho no editor, ele pode ser o primeiro —
                  abra a aba <strong>Meus trabalhos</strong> e publique.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {publicacoes.map((publicacao) => (
                <Card key={publicacao.id} className="flex flex-col">
                  <CardHeader>
                    <div className="flex flex-wrap items-center gap-2 pb-1">
                      <Badge tom="principal">{ROTULO_NIVEL_DA_BIBLIOTECA[publicacao.nivel].split(' (')[0]}</Badge>
                      <span className="text-[11px] text-muted-foreground">{publicacao.ano}</span>
                      {publicacao.ano && publicacao.instituicao ? (
                        <span className="text-[11px] text-muted-foreground">· {publicacao.instituicao}</span>
                      ) : null}
                      {publicacao.gratuito ? <Badge tom="sucesso">Aberto</Badge> : null}
                    </div>
                    <CardTitle className="text-base leading-snug">{publicacao.titulo}</CardTitle>
                    <CardDescription>
                      {publicacao.autores}
                      {publicacao.curso ? ` · ${publicacao.curso}` : ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col gap-3">
                    <p className="line-clamp-5 text-xs leading-relaxed text-muted-foreground">{publicacao.resumo}</p>
                    {publicacao.palavrasChave.length > 0 ? (
                      <p className="text-[11px] text-muted-foreground">
                        <strong className="text-foreground">Palavras-chave:</strong>{' '}
                        {publicacao.palavrasChave.join('; ')}
                      </p>
                    ) : null}
                    <div className="mt-auto flex items-center justify-between gap-2 pt-2">
                      <span className="text-xs font-medium">
                        {publicacao.precoAPagar === 0 ? 'Aberto, sem cobrança' : precoFormatado(publicacao.precoAPagar)}
                      </span>
                      <Button asChild variant="contorno" tamanho="pequeno">
                        <Link to={`/biblioteca/${publicacao.id}`}>
                          Ler o resumo
                          <ExternalLink />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3 text-sm">
            <span className="flex items-center gap-1.5 font-medium">
              <Wallet className="size-4 text-primary" />
              A receber
            </span>
            <span className="text-muted-foreground">{precoFormatado(totalAReceber)}</span>
            <span className="text-[11px] text-muted-foreground">
              O repasse é feito por Pix pelo administrador; o valor aparece aqui até ser transferido.
            </span>
          </div>

          {minhas === null ? (
            <div className="flex min-h-[20vh] items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span className="text-sm">Carregando seus trabalhos...</span>
            </div>
          ) : minhas.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Você ainda não publicou nada</CardTitle>
                <CardDescription>
                  Publique um trabalho seu em <strong>Documentos</strong>: na lista, cada documento tem o
                  botão <strong>Publicar na biblioteca</strong>.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="contorno">
                  <Link to="/documentos">
                    <FileText />
                    Ir para os meus documentos
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {minhas.map((publicacao) => (
                <Card key={publicacao.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-center gap-2 pb-1">
                      <Badge tom={publicacao.estado === 'PUBLICADA' ? 'sucesso' : 'neutro'}>
                        {ROTULO_ESTADO_DA_PUBLICACAO[publicacao.estado]}
                      </Badge>
                      {publicacao.gratuito ? <Badge tom="neutro">Sem cobrança</Badge> : null}
                    </div>
                    <CardTitle className="text-base">{publicacao.titulo}</CardTitle>
                    <CardDescription>
                      {publicacao.vendas.consultas} consulta(s) · {precoFormatado(publicacao.vendas.recebidoCentavos ?? 0)}{' '}
                      já repassado(s) · {precoFormatado(publicacao.vendas.aReceberCentavos ?? 0)} a receber
                    </CardDescription>
                  </CardHeader>
                  {publicacao.estado === 'PUBLICADA' ? (
                    <CardContent className="flex flex-wrap gap-2">
                      <Button asChild variant="contorno" tamanho="pequeno">
                        <Link to={`/biblioteca/${publicacao.id}`}>
                          <ExternalLink />
                          Ver como o leitor vê
                        </Link>
                      </Button>
                      <Button
                        variant="fantasma"
                        tamanho="pequeno"
                        onClick={async () => {
                          await api.atualizarPublicacao(publicacao.id, { arquivar: true });
                          await carregarMinhas();
                        }}
                      >
                        Tirar do ar
                      </Button>
                    </CardContent>
                  ) : null}
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
        <Star className="mr-1 inline size-3" />
        Quem publica recebe pela consulta e pode pedir uma contribuição. O repasse é feito por Pix, depois
        que o administrador confere o mês.
      </p>
    </div>
  );
}
